import { defineStore } from 'pinia';
import { notify } from 'notiwind';
import dayjs from 'dayjs';
import { handleError, normalizeError } from '../utils/error-handler';

let redemptionFormDataInFlight = null;

export const useTripsStore = defineStore('trips', {
    state: () => ({
        activeRedemption: {
            booking_date: null,
            class_of_service: null,
            companion_pass_used: false,
            connected_account_id: null,
            end_date: null,
            flexible_points_transfer: false,
            id: null,
            isSouthwestFlight: false,
            name: null,
            number_of_travelers: 1,
            redemption_type: {
                slug: null,
            },
            redemption_type_id: null,
            start_date: null,
            transfers: [
                {
                    transfer_points: 0,
                    transfer_bonus_percentage: 0,
                    transfer_program_id: null,
                },
            ],
            travel_segments: [
                {
                    destination: null,
                    origin: null,
                    travel_number: null,
                },
            ],
            uuid: null,
        },
        activeTrip: {
            city: null,
            country: null,
            country_short: null,
            description: null,
            end_date: null,
            hide_sensitive_share: false,
            id: null,
            image: null,
            latitude: null,
            longitude: null,
            start_date: null,
            state: null,
            title: null,
            uuid: null,
        },
        classOptions: [
            { label: 'Economy', value: 'economy' },
            { label: 'Premium Economy', value: 'premium_economy' },
            { label: 'Mixed Class of Service', value: 'mixed' },
            { label: 'Business Class', value: 'business' },
            { label: 'First Class', value: 'first' },
        ],
        currentPage: 1,
        perPage: 10,
        errors: [],
        loading: false,
        panelRedemption: null,
        pastTrips: [],
        pastTripsPage: 1,
        pastTripsPages: 0,
        pastTripsTotal: 0,
        paymentTypes: [
            { value: 'cash', label: 'Cash', icon: 'coin' },
            { value: 'points', label: 'Points', icon: 'points' },
            {
                value: 'cash_points',
                label: 'Cash and points',
                icon: 'cash-points',
            },
            { value: 'other', label: 'Other', icon: 'circle-dots' },
        ],
        programs: [],
        redemptionStep: 1,
        redemptionTypes: [],
        redemptions: [],
        showDeleteRedemptionConfirm: false,
        showEmptyRedemptionsState: false,
        showEmptyTripState: false,
        showRedemptionCreateModal: false,
        showRedemptionEditModal: false,
        showRedemptionPanel: false,
        editingUnfiledRedemption: false,
        pendingAttachRedemptionId: null,
        attachAfterEditRedemption: null,
        showTripModal: false,
        steps: [
            {
                id: 0,
                name: 'Select redemption type',
                step: 1,
            },
            {
                id: 1,
                name: 'Fill redemption details',
                step: 2,
            },
            {
                id: 2,
                name: 'Adding redemption',
                step: 3,
            },
        ],
        transferPartners: [],
        travelPortals: [],
        trip: null,
        tripFormErrors: {},
        trips: {
            data: [],
            total: 0,
        },
        upcomingTrips: [],
        upcomingTripsPage: 1,
        upcomingTripsPages: 0,
        upcomingTripsTotal: 0,
        wallet: null,
        archiveStats: null,
        archiveStatsYear: null,
        archiveStatsLoading: false,
    }),
    getters: {
        classOption(state) {
            return state.classOptions.find(
                (c) => c.value === state.activeRedemption.class_of_service
            );
        },
        connectedAccount(state) {
            return state.wallet?.users
                .flatMap((user) => user.institutions)
                .flatMap((ins) => ins.accounts)
                .find(
                    (account) =>
                        account.id ===
                        this.activeRedemption.connected_account_id
                );
        },
        endRange(state) {
            const end = state.currentPage * state.perPage;
            return end > state.trips.total ? state.trips.total : end;
        },
        panelPaymentType(state) {
            return state.paymentTypes.find(
                (type) => type.value === state.panelRedemption?.payment_type
            );
        },
        paymentType(state) {
            return state.paymentTypes.find(
                (type) => type.value === state.activeRedemption.payment_type
            );
        },
        program(state) {
            return state.programs.find(
                (program) =>
                    program.id === state.activeRedemption.payment_program_id
            );
        },
        redemptionType(state) {
            return state.redemptionTypes.find(
                (type) => type.id === state.activeRedemption.redemption_type_id
            );
        },
        startRange(state) {
            return (state.currentPage - 1) * state.perPage + 1;
        },
        transferProgram(state) {
            return state.programs.find(
                (program) =>
                    program.id === state.activeRedemption.transfer_program_id
            );
        },
        totalPages(state) {
            return Math.ceil(state.trips.total / state.perPage);
        },
    },
    actions: {
        createRedemption() {
            this.errors = [];
            this.tripFormErrors = {};

            if (!this.activeRedemption.flexible_points_transfer) {
                this.setRedemptionTransfers([]);
            }

            return ApiService.apiCall(
                `/trips/${this.trip.uuid}/redemptions`,
                'post',
                this.activeRedemption
            )
                .then((response) => {
                    if (!response || response.status !== 200) {
                        throw new Error(response);
                    }

                    this.redemptionStep = 1;
                    this.resetActiveRedemptionData();
                    this.setShowRedemptionCreateModal(false);
                    notify({
                        group: 'success',
                        title: 'Redemption created',
                        message: `Redemption has been successfully created`,
                    });
                    this.getRedemptions(this.trip.uuid);
                    this.handleTripDateChange(response.data);
                    this.getArchiveStats(this.archiveStatsYear);
                    return response;
                })
                .catch((error) => {
                    // Error handling
                    const errorMsg = error.message || 'An error occurred';

                    const normalizedError = normalizeError(error);
                    if (
                        normalizedError.status === 422 &&
                        normalizedError.data?.errors
                    ) {
                        this.tripFormErrors = normalizedError.data.errors;
                    }

                    notify({
                        group: 'error',
                        title: 'Error saving redemption',
                        message: `There was an error saving the redemption`,
                    });
                    return error;
                });
        },
        createTrip() {
            this.loading = true;
            this.errors = [];
            this.tripFormErrors = {};
            this.validateTrip();
            return ApiService.apiCall('/trips', 'post', this.activeTrip)
                .then((response) => {
                    notify({
                        group: 'success',
                        title: 'Trip saved',
                        text: 'Trip has been successfully created',
                    });
                    this.resetActiveTripData();
                    this.loading = false;
                    this.getTrips();
                    return response;
                })
                .catch((error) => {
                    const normalizedError = normalizeError(error);
                    this.tripFormErrors = normalizedError.data?.errors || {};
                    this.loading = false;
                    throw error;
                });
        },
        deleteRedemption(uuid) {
            ApiService.apiCall(
                `/trips/${this.trip.uuid}/redemptions/${uuid}`,
                'delete'
            )
                .then(async (response) => {
                    this.closeRedemptionEditModal();
                    this.setShowRedemptionPanel(false);
                    this.setShowDeleteRedemptionConfirm(false);
                    // await this.getTrip(this.trip.uuid);
                    this.getRedemptions(this.trip.uuid);
                    this.getArchiveStats(this.archiveStatsYear);
                    notify({
                        group: 'success',
                        title: 'Redemption deleted',
                        text: 'Redemption has been successfully deleted',
                    });
                })
                .catch((error) => {
                    handleError(error, {
                        defaultTitle: 'Error deleting redemption',
                    });
                });
        },
        async deleteTrip(uuid) {
            try {
                await ApiService.apiCall(`/trips/${uuid}`, 'delete');

                const upcomingTripsIndex = this.upcomingTrips.findIndex(
                    (trip) => trip.uuid === uuid
                );
                const pastTripsIndex = this.pastTrips.findIndex(
                    (trip) => trip.uuid === uuid
                );

                if (upcomingTripsIndex !== -1) {
                    this.upcomingTrips.splice(upcomingTripsIndex, 1);
                }
                if (pastTripsIndex !== -1) {
                    this.pastTrips.splice(pastTripsIndex, 1);
                }

                this.getArchiveStats(this.archiveStatsYear);
                notify({
                    group: 'success',
                    title: 'Trip deleted',
                    text: 'Trip has been successfully deleted',
                });
            } catch (error) {
                notify({
                    group: 'error',
                    title: 'Error deleting trip',
                    text: 'There was an error deleting the trip',
                });
            }
        },
        getRedemptions(uuid) {
            // Validate UUID parameter to prevent null/undefined API calls
            if (!uuid || uuid === 'null' || uuid === 'undefined') {
                console.error('Invalid UUID provided to getRedemptions:', uuid);
                this.loading = false;
                return Promise.reject(new Error('Invalid trip UUID provided'));
            }

            this.showEmptyRedemptionsState = false;
            this.loading = true;
            ApiService.apiCall(`/trips/${uuid}/redemptions`, 'get')
                .then(async (response) => {
                    this.redemptions = response.data.redemptions;
                    await this.getTrip(uuid);
                    this.showEmptyRedemptionsState = true;
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    handleError(error, {
                        defaultTitle: 'Error getting redemptions',
                    });
                });
        },
        async getTrip(uuid) {
            // Validate UUID parameter to prevent null/undefined API calls
            if (!uuid || uuid === 'null' || uuid === 'undefined') {
                console.error('Invalid UUID provided to getTrip:', uuid);
                return Promise.reject(new Error('Invalid trip UUID provided'));
            }

            const upcomingTrip = this.upcomingTrips.find(
                (trip) => trip.uuid === uuid
            );
            const pastTrip = this.pastTrips.find((trip) => trip.uuid === uuid);

            if (upcomingTrip) {
                this.trip = upcomingTrip;
                this.setActiveTrip(this.trip);
            }
            if (pastTrip) {
                this.trip = pastTrip;
                this.setActiveTrip(this.trip);
            }

            return ApiService.apiCall(`/trips/${uuid}`, 'get')
                .then((response) => {
                    this.trip = this.setActiveTrip(response.data.trip);
                    let tripIndex = this.upcomingTrips.findIndex(
                        (item) => item.uuid === uuid
                    );
                    if (tripIndex === -1) {
                        tripIndex = this.pastTrips.findIndex(
                            (item) => item.uuid === uuid
                        );
                        if (tripIndex !== -1) {
                            this.pastTrips[tripIndex] = this.trip;
                        }
                    } else {
                        this.upcomingTrips[tripIndex] = this.trip;
                    }
                    this.redemptionTypes = response.data.redemption_types;
                    this.programs = response.data.programs;
                    this.transferPartners = response.data.transfer_partners;
                    this.wallet = response.data.wallet;
                    return response.data.trip;
                })
                .catch((error) => {
                    handleError(error, {
                        defaultTitle: 'Error getting trip',
                    });
                });
        },
        getTravelPortals() {
            this.loading = true;
            ApiService.apiCall('/travel-portal', 'get')
                .then((response) => {
                    this.travelPortals = response.data;
                })
                .catch((error) => {
                    handleError(error, {
                        defaultTitle: 'Error getting travel portals',
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getRedemptionFormData() {
            if (redemptionFormDataInFlight) {
                return redemptionFormDataInFlight;
            }
            redemptionFormDataInFlight = ApiService.apiCall(
                '/redemptions/form-data',
                'get'
            )
                .then((response) => {
                    this.redemptionTypes = response.data.redemption_types;
                    this.programs = response.data.programs;
                    this.transferPartners = response.data.transfer_partners;
                    this.wallet = response.data.wallet;
                    return response.data;
                })
                .catch((error) => {
                    handleError(error, {
                        defaultTitle: 'Error loading form data',
                    });
                })
                .finally(() => {
                    redemptionFormDataInFlight = null;
                });
            return redemptionFormDataInFlight;
        },
        getArchiveStats(year = null) {
            this.archiveStatsLoading = true;
            const url = year ? `/trips/stats?year=${year}` : '/trips/stats';
            return ApiService.apiCall(url, 'get')
                .then((response) => {
                    this.archiveStats = response.data;
                    this.archiveStatsYear = year;
                })
                .catch((error) => {
                    handleError(error, {
                        defaultTitle: 'Error loading stats',
                    });
                })
                .finally(() => {
                    this.archiveStatsLoading = false;
                });
        },
        getTrips() {
            this.showEmptyTripState = false;
            this.loading = true;
            ApiService.apiCall('/trips', 'get')
                .then((response) => {
                    this.upcomingTrips = response.data.upcoming_trips.data;
                    this.pastTrips = response.data.past_trips.data;
                    this.upcomingTripsPage =
                        response.data.upcoming_trips.current_page;
                    this.pastTripsPage = response.data.past_trips.current_page;
                    this.upcomingTripsPages =
                        response.data.upcoming_trips.last_page;
                    this.pastTripsPages = response.data.past_trips.last_page;
                    this.upcomingTripsTotal =
                        response.data.upcoming_trips.total;
                    this.pastTripsTotal = response.data.past_trips.total;
                    this.perPage = response.data.upcoming_trips.per_page;
                    this.loading = false;
                    this.showEmptyTripState = true;
                })
                .catch((error) => {
                    this.loading = false;
                });
        },
        handleTripDateChange(data) {
            if (data.trip_date_updated) {
                this.trip.start_date = data.start_date;
                this.trip.end_date = data.end_date;

                notify(
                    {
                        group: 'info',
                        title: 'Updated Trip Date',
                        message: `We updated your trip date to ${dayjs(this.trip.start_date).format('MMM DD,YYYY')} - ${dayjs(this.trip.end_date).format('MMM DD, YYYY')} because the redemption date was outside of the trip date.`,
                    },
                    10000
                );
            }
        },
        setActiveTrip(trip) {
            this.activeTrip = Object.assign({}, trip);

            this.activeTrip.start_date = trip.start_date
                ? dayjs(trip.start_date).format('MM/DD/YYYY')
                : null;
            this.activeTrip.end_date = trip.end_date
                ? dayjs(trip.end_date).format('MM/DD/YYYY')
                : null;
            return this.activeTrip;
        },
        setConnectedAccount(id) {
            this.activeRedemption.connected_account_id = id;
        },
        setCurrentPage(page) {
            this.currentPage = page;
        },

        setActiveRedemption(redemption) {
            this.activeRedemption = JSON.parse(JSON.stringify(redemption));

            // Ensure at least one empty travel segment for flight/train types
            const typeSlug = this.redemptionTypes?.find(
                (t) => t.id === this.activeRedemption.redemption_type_id
            )?.slug;
            if (
                (typeSlug === 'flight' || typeSlug === 'train') &&
                (!this.activeRedemption.travel_segments || this.activeRedemption.travel_segments.length === 0)
            ) {
                this.activeRedemption.travel_segments = [{
                    origin: null,
                    origin_name: null,
                    destination: null,
                    destination_name: null,
                    travel_number: null,
                }];
            }

            if (redemption?.uuid) {
                const fullRedemption = this.redemptions.find(
                    (r) => r.uuid === redemption.uuid
                );

                if (fullRedemption?.files?.length) {
                    this.activeRedemption.files = [...fullRedemption.files];
                    this.activeRedemption.attachments = [
                        ...fullRedemption.files,
                    ];
                    return;
                }
            }

            if (
                this.activeRedemption.attachments?.length > 0 &&
                (!this.activeRedemption.files ||
                    this.activeRedemption.files.length === 0)
            ) {
                this.activeRedemption.files = [
                    ...this.activeRedemption.attachments,
                ];
            } else if (
                this.activeRedemption.files?.length > 0 &&
                (!this.activeRedemption.attachments ||
                    this.activeRedemption.attachments.length === 0)
            ) {
                this.activeRedemption.attachments = [
                    ...this.activeRedemption.files,
                ];
            }
        },
        setPanelRedemption(redemption) {
            this.panelRedemption = redemption;
        },
        setPerPage(perPage) {
            this.perPage = perPage;
        },
        setPlace(place) {
            this.activeTrip.city = place.addressTypes?.locality
                ? place.addressTypes.locality.longName
                : null;
            this.activeTrip.state = place.addressTypes
                ?.administrative_area_level_1?.longName
                ? place.addressTypes?.administrative_area_level_1?.longName
                : place.addressTypes?.administrative_area_level_1?.shortName ||
                  null;
            this.activeTrip.country =
                place.addressTypes?.country?.longName || null;
            this.activeTrip.country_short =
                place.addressTypes?.country?.shortName || null;
            this.activeTrip.latitude = place.lat;
            this.activeTrip.longitude = place.lng;
        },
        clearPlace() {
            this.activeTrip.city = null;
            this.activeTrip.state = null;
            this.activeTrip.country = null;
            this.activeTrip.country_short = null;
            this.activeTrip.latitude = null;
            this.activeTrip.longitude = null;
        },
        setRedemptionStep(step) {
            this.redemptionStep = step;
        },
        setRedemptionTransfers(transfers) {
            this.activeRedemption.transfers = transfers;
        },
        setSegments(segments) {
            this.activeRedemption.travel_segments = segments;
        },
        setShowDeleteRedemptionConfirm(val) {
            this.showDeleteRedemptionConfirm = val;
        },
        setShowRedemptionCreateModal(value) {
            if (value) {
                this.resetActiveRedemptionData();
                this.redemptionStep = 1;
                this.clearErrors();
            }
            this.showRedemptionCreateModal = value;
            if (!value) {
                this.resetActiveRedemptionData();
            }
        },
        setShowRedemptionEditModal(value) {
            if (!value) {
                this.resetActiveRedemptionData();
                this.editingUnfiledRedemption = false;
            }
            this.showRedemptionEditModal = value;
        },
        setEditingUnfiledRedemption(value) {
            this.editingUnfiledRedemption = value;
        },
        setPendingAttachRedemptionId(id) {
            this.pendingAttachRedemptionId = id;
        },
        setAttachAfterEditRedemption(redemption) {
            this.attachAfterEditRedemption = redemption;
        },
        setShowRedemptionPanel(value) {
            this.showRedemptionPanel = value;
        },
        setShowTripModal(value) {
            this.showTripModal = value;
        },
        setError(err) {
            // {message: 'Error message'}
            this.errors.push(err);
        },
        setFormError(key, message) {
            this.tripFormErrors[key] = message;
        },
        clearErrors() {
            this.errors = [];
            this.tripFormErrors = {};
        },
        closeModal() {
            this.activeRedemption.redemption_type_id = null;
            this.redemptionStep = 1;
            this.errors = [];
            this.tripFormErrors = {};
            this.setShowRedemptionCreateModal(false);
        },
        closeRedemptionEditModal() {
            this.errors = [];
            this.tripFormErrors = {};
            this.redemptionStep = 1;
            this.setShowRedemptionEditModal(false);
            // Only refresh trip redemptions if not editing an unfiled redemption
            if (!this.editingUnfiledRedemption && this.trip?.uuid) {
                this.getRedemptions(this.trip.uuid);
            }
        },
        resetActiveRedemptionData() {
            this.activeRedemption = {
                booking_date: null,
                class_of_service: null,
                companion_pass_used: false,
                connected_account_id: null,
                end_date: null,
                flexible_points_transfer: false,
                id: null,
                isSouthwestFlight: false,
                name: null,
                number_of_travelers: 1,
                redemption_type: {
                    slug: null,
                },
                redemption_type_id: null,
                start_date: null,
                transfers: [
                    {
                        transfer_points: 0,
                        transfer_bonus_percentage: 0,
                        transfer_program_id: null,
                    },
                ],
                travel_segments: [
                    {
                        destination: null,
                        origin: null,
                        travel_number: null,
                    },
                ],
                uuid: null,
            };
        },
        resetActiveTripData() {
            this.activeTrip = {
                city: null,
                country: null,
                country_short: null,
                description: null,
                end_date: null,
                hide_sensitive_share: false,
                id: null,
                image: null,
                latitude: null,
                longitude: null,
                start_date: null,
                state: null,
                title: null,
                uuid: null,
            };
        },
        tripsPageChange(page, type) {
            const endpoint = type === 'upcoming' ? 'upcoming' : 'past';
            this.loading = true;
            ApiService.apiCall('/trips/' + endpoint + '?page=' + page, 'get')
                .then((response) => {
                    if (endpoint === 'upcoming') {
                        this.upcomingTrips = response.data.upcoming_trips.data;
                        this.upcomingTripsPage =
                            response.data.upcoming_trips.current_page;
                    }

                    if (endpoint === 'past') {
                        this.pastTrips = response.data.past_trips.data;
                        this.pastTripsPage =
                            response.data.past_trips.current_page;
                    }

                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                });
        },
        updateRedemption(params) {
            if (!params?.attachmentsOnly) {
                this.validateRedemption();

                if (Object.keys(this.tripFormErrors).length > 0) {
                    return;
                }

                if (!this.activeRedemption.flexible_points_transfer) {
                    this.setRedemptionTransfers([]);
                }
            }

            // Use different endpoint for unfiled redemptions (no trip context)
            const apiUrl = this.editingUnfiledRedemption
                ? `/redemptions/${this.activeRedemption.id}`
                : `/trips/${this.trip.uuid}/redemptions/${this.activeRedemption.uuid}`;

            return ApiService.apiCall(apiUrl, 'put', this.activeRedemption)
                .then((response) => {
                    if (!response || response.status !== 200) {
                        throw new Error(response);
                    }
                    const updatedRedemption = response.data.redemption;

                    if (
                        (!updatedRedemption.attachments ||
                            updatedRedemption.attachments.length === 0) &&
                        (!updatedRedemption.files ||
                            updatedRedemption.files.length === 0) &&
                        this.activeRedemption.attachments &&
                        this.activeRedemption.attachments.length > 0
                    ) {
                        updatedRedemption.attachments = [
                            ...this.activeRedemption.attachments,
                        ];
                        updatedRedemption.files = [
                            ...this.activeRedemption.attachments,
                        ];
                    }

                    if (
                        updatedRedemption.attachments &&
                        !updatedRedemption.files
                    ) {
                        updatedRedemption.files = [
                            ...updatedRedemption.attachments,
                        ];
                    } else if (
                        updatedRedemption.files &&
                        !updatedRedemption.attachments
                    ) {
                        updatedRedemption.attachments = [
                            ...updatedRedemption.files,
                        ];
                    }

                    if (this.showRedemptionPanel) {
                        this.panelRedemption = Object.assign(
                            {},
                            updatedRedemption
                        );
                    }

                    if (!params?.keepEditModalOpen) {
                        this.setShowRedemptionEditModal(false);
                    }

                    if (!params?.silent) {
                        notify({
                            group: 'success',
                            title: 'Redemption updated',
                            message: `Redemption has been successfully updated`,
                        });
                    }

                    // Only refresh trip redemptions if we're not editing an unfiled redemption
                    if (!this.editingUnfiledRedemption && this.trip?.uuid) {
                        this.getRedemptions(this.trip.uuid);
                        this.handleTripDateChange(response.data);
                    }
                    this.getArchiveStats(this.archiveStatsYear);
                    return response;
                })
                .catch((error) => {
                    notify({
                        group: 'error',
                        title: 'Error saving redemption',
                        message: `There was an error saving the redemption. Please try again.`,
                    });
                    return error;
                });
        },
        updateTrip(params) {
            this.loading = true;
            this.errors = [];
            this.tripFormErrors = {};

            this.validateTrip();

            return ApiService.apiCall(
                `/trips/${this.activeTrip.uuid}`,
                'post',
                this.activeTrip
            )
                .then((response) => {
                    if (response?.data?.trip) {
                        const upcomingTripsIndex = this.upcomingTrips.findIndex(
                            (trip) => trip.id === response.data.trip.id
                        );

                        const pastTripsIndex = this.pastTrips.findIndex(
                            (trip) => trip.id === response.data.trip.id
                        );

                        if (upcomingTripsIndex !== -1) {
                            this.upcomingTrips[upcomingTripsIndex] =
                                response.data.trip;
                        }

                        if (pastTripsIndex !== -1) {
                            this.pastTrips[pastTripsIndex] = response.data.trip;
                        }

                        this.setActiveTrip(response.data.trip);
                    }

                    notify({
                        group: 'success',
                        title: params?.title || 'Trip updated',
                        text:
                            params?.message ||
                            'Trip has been successfully updated',
                    });

                    this.loading = false;
                    return response;
                })
                .catch((error) => {
                    const normalizedError = normalizeError(error);
                    this.tripFormErrors = normalizedError.data?.errors || {};
                    this.loading = false;
                    throw error;
                });
        },
        validateRedemption() {
            this.clearErrors();

            if (!this.activeRedemption.redemption_type_id) {
                this.setFormError(
                    'redemption_type_id',
                    'Please select a redemption type to proceed'
                );
            }

            if (this.redemptionStep === 2) {
                if (!this.activeRedemption.start_date) {
                    this.setFormError(
                        'start_date',
                        'Please enter a travel/departure date'
                    );
                }

                // Validate "Other" redemption type specific fields
                const redemptionType = this.redemptionTypes.find(
                    (rt) => rt.id === this.activeRedemption.redemption_type_id
                );
                if (redemptionType?.slug === 'other') {
                    if (
                        !this.activeRedemption.name ||
                        this.activeRedemption.name.trim() === ''
                    ) {
                        this.setFormError(
                            'name',
                            'Please enter a redemption name'
                        );
                    }
                }
            }

            if (this.redemptionStep === 3) {
                if (this.paymentType?.value === 'points') {
                    if (!this.activeRedemption.payment_points) {
                        this.setFormError(
                            'payment_points_used',
                            'Please enter the points used'
                        );
                    }
                    if (!this.activeRedemption.payment_program_id) {
                        this.setFormError(
                            'payment_program_id',
                            'Please choose a points currency'
                        );
                    }
                }

                if (this.activeRedemption.flexible_points_transfer) {
                    this.validateTransfers();
                }

                if (!this.paymentType) {
                    this.setFormError(
                        'payment_type',
                        'Please select a payment method to proceed'
                    );
                }
                if (
                    this.paymentType?.value === 'cash' &&
                    !this.activeRedemption.advertised_price
                ) {
                    this.setFormError(
                        'advertised_price',
                        'Please enter your advertised price'
                    );
                }
                if (this.paymentType?.value === 'cash_points') {
                    if (!this.activeRedemption.payment_cash_amount) {
                        this.setFormError(
                            'payment_cash_amount',
                            'Please enter your payment cash cost'
                        );
                    }
                    if (!this.activeRedemption.payment_taxes_fees) {
                        this.setFormError(
                            'payment_taxes_fees',
                            'Please enter your payment taxes and fees'
                        );
                    }

                    if (!this.activeRedemption.payment_points) {
                        this.setFormError(
                            'payment_points_used',
                            'Please enter your points used'
                        );
                    }
                    if (!this.activeRedemption.payment_program_id) {
                        this.setFormError(
                            'payment_program_id',
                            'Please select your points currency'
                        );
                    }
                }
                if (this.activeRedemption.usedTravelPortal) {
                    if (!this.activeRedemption.travel_portal_id) {
                        this.setFormError(
                            'travel_portal_id',
                            'Please choose a travel portal'
                        );
                    }
                    if (
                        this.activeRedemption.travel_portal_id === 8 &&
                        !this.activeRedemption.travel_portal_custom_name
                    ) {
                        this.setFormError(
                            'travel_portal_custom_name',
                            'Please enter the name of the portal you used.'
                        );
                    }
                }
            }
        },
        validateTransfers() {
            const isValid = (t) => {
                return t.transfer_points > 0 && t.transfer_program_id;
            };
            if (!this.activeRedemption.transfers.every(isValid)) {
                this.setFormError(
                    'transfers',
                    'All transfers must have a selected program and points entered.'
                );
            }
        },
        validateTrip() {},
    },
});
